import React, { useState } from 'react';

import type { OficinaExterna } from '../../../api/oficinaExternaApi';
import { fetchEventoLookup } from '../../../api/eventosApi';
import { notify } from '../../../shared/feedback/notify';
import { hojeIso } from '../formatacao';
import '../../admin-sistema/pages/css/adminSistema.css';
import '../pages/OficinaExterna.css';
import '../../../shared/feedback/erro-de-tela.css';

export interface DadosDoCadastro {
  eventNumber: number;
  placa: string;
  classe: string;
  modelo?: string;
  cor?: string;
  anoModelo?: string;
  entradaEm: string;
}

interface Props {
  oficina: OficinaExterna;
  online: boolean;
  /** Placas já na árvore (servidor ou fila): recusadas aqui, antes de virar item na fila. */
  placasJaNaArvore: string[];
  onCancelar: () => void;
  onSalvar: (dados: DadosDaVisitaCadastro) => Promise<void>;
}

type DadosDaVisitaCadastro = DadosDoCadastro;

const CLASSES = [
  { valor: 'ASSOCIADO', rotulo: 'Associado' },
  { valor: 'TERCEIRO', rotulo: 'Terceiro' },
  { valor: 'MOBI', rotulo: 'Mobi' },
  { valor: 'CARROS_EXTERNOS', rotulo: 'Carro externo' },
  { valor: 'CARROS_ATUAL', rotulo: 'Carro Atual' },
  { valor: 'MOTO', rotulo: 'Moto' },
];

const normalizarPlaca = (valor: string) => valor.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * Cadastro de um veículo numa oficina externa (at-114, ADR 0059).
 *
 * 🔴 Funciona SEM REDE (decisão do Victor, 24/09/2026): o número do evento é digitado e o
 * cadastro vai para a fila. O lookup do SGA é um FACILITADOR, não um pré-requisito — exigi-lo
 * deixaria o inspetor sem cadastrar o veículo que ele está vendo, por falta de sinal.
 *
 * ⚠️ A placa repetida é recusada AQUI, antes de virar item de fila, quando ela já está na árvore
 * do próprio aparelho. A recusa do servidor (outro inspetor cadastrou) continua existindo e é
 * tratada na tela da fila, com a opção de anexar as visitas ao cadastro do colega.
 */
const CadastroForm: React.FC<Props> = ({
  oficina,
  online,
  placasJaNaArvore,
  onCancelar,
  onSalvar,
}) => {
  const [evento, setEvento] = useState('');
  const [placa, setPlaca] = useState('');
  const [classe, setClasse] = useState('ASSOCIADO');
  const [modelo, setModelo] = useState('');
  const [cor, setCor] = useState('');
  const [anoModelo, setAnoModelo] = useState('');
  const [entradaEm, setEntradaEm] = useState(hojeIso());
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const buscarNoEvento = async () => {
    const numero = Number(evento);
    if (!Number.isFinite(numero) || numero <= 0) {
      setErro('Informe o número do evento antes de buscar.');
      return;
    }

    setBuscando(true);
    try {
      const resposta = await fetchEventoLookup(String(numero));

      if (resposta.hinovaStatus !== 'ok' || !resposta.veiculos?.length) {
        // Estado legítimo (evento sem veículo aproveitável) e falha de consulta são ditos de
        // formas diferentes — a regra da at-60 vale aqui como em qualquer tela.
        notify.warning(
          resposta.hinovaStatus === 'indisponivel'
            ? 'Não foi possível consultar o evento agora. Preencha os dados à mão.'
            : 'O evento não retornou veículos. Preencha os dados à mão.',
        );
        return;
      }

      const veiculo = resposta.veiculos[0];
      setPlaca(normalizarPlaca(veiculo.plate ?? ''));
      setClasse(veiculo.tipo === 'TERCEIRO' ? 'TERCEIRO' : 'ASSOCIADO');
      setModelo(veiculo.model ?? '');
      setCor(veiculo.color ?? '');
      setAnoModelo(veiculo.modelYear ? String(veiculo.modelYear) : '');
      notify.success('Dados do evento preenchidos. Confira antes de salvar.');
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível consultar o evento. Preencha os dados à mão.');
    } finally {
      setBuscando(false);
    }
  };

  const salvar = async () => {
    const numero = Number(evento);
    const placaCanonica = normalizarPlaca(placa);

    if (!Number.isFinite(numero) || numero <= 0) {
      setErro('O número do evento é obrigatório.');
      return;
    }
    if (placaCanonica.length < 7) {
      setErro('Informe a placa completa.');
      return;
    }
    if (placasJaNaArvore.includes(placaCanonica)) {
      setErro(
        `A placa ${placaCanonica} já está cadastrada em uma oficina. Procure-a na árvore e ` +
          'registre a visita no cadastro que já existe.',
      );
      return;
    }

    setErro(null);
    setSalvando(true);
    try {
      await onSalvar({
        eventNumber: numero,
        placa: placaCanonica,
        classe,
        modelo: modelo.trim() || undefined,
        cor: cor.trim() || undefined,
        anoModelo: anoModelo.trim() || undefined,
        entradaEm: new Date(`${entradaEm}T12:00:00`).toISOString(),
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="oe-form">
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      {/* Seção 1 — o evento. Número e busca são UM gesto: ficam na mesma linha, o botão
          alinhado à base do campo (`admin-form-grid`). O verbo da ação vive no título da tela
          (`recepcao-title`); o nome da oficina é o contexto, na hint do card. */}
      <div className="admin-card">
        <h3 className="admin-card-title">Evento</h3>
        <p className="admin-hint">
          Cadastro em <strong>{oficina.nome}</strong>. O número do evento é o que liga este veículo
          ao atendimento da Atual.
        </p>

        <div className="admin-form-grid">
          <div className="admin-input-group curto">
            <label className="admin-label oe-campo-obrigatorio" htmlFor="oe-cad-evento">
              Número do evento
            </label>
            <input
              id="oe-cad-evento"
              className="admin-input"
              type="number"
              inputMode="numeric"
              value={evento}
              onChange={(e) => setEvento(e.target.value)}
              placeholder="Ex: 90210"
            />
          </div>
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            disabled={!online || buscando || !evento}
            title={
              online
                ? 'Preenche placa, modelo e classe a partir do evento'
                : 'Sem rede: preencha os dados à mão. O evento é conferido quando o cadastro subir.'
            }
            onClick={buscarNoEvento}
          >
            {buscando ? 'Buscando...' : 'Buscar no evento'}
          </button>
        </div>

        {!online && (
          <p className="admin-hint" style={{ marginTop: 12 }}>
            Sem rede: a busca pelo evento não está disponível agora. Preencha os dados à mão — o
            número do evento é conferido quando o cadastro for enviado.
          </p>
        )}
      </div>

      {/* Seção 2 — o veículo. Duas linhas no desktop (placa + classe; modelo + cor + ano), e a
          entrada sozinha; no celular o `admin-form-grid` empilha tudo. */}
      <div className="admin-card">
        <h3 className="admin-card-title">Veículo</h3>

        <div className="admin-form-grid">
          <div className="admin-input-group curto">
            <label className="admin-label oe-campo-obrigatorio" htmlFor="oe-cad-placa">
              Placa
            </label>
            <input
              id="oe-cad-placa"
              className="admin-input"
              type="text"
              value={placa}
              maxLength={10}
              autoCapitalize="characters"
              onChange={(e) => setPlaca(normalizarPlaca(e.target.value))}
              placeholder="ABC1D23"
            />
          </div>
          <div className="admin-input-group curto">
            <label className="admin-label oe-campo-obrigatorio" htmlFor="oe-cad-classe">
              Classe
            </label>
            <select
              id="oe-cad-classe"
              className="admin-select"
              value={classe}
              onChange={(e) => setClasse(e.target.value)}
            >
              {CLASSES.map((c) => (
                <option key={c.valor} value={c.valor}>
                  {c.rotulo}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="admin-form-grid" style={{ marginTop: 16 }}>
          <div className="admin-input-group">
            <label className="admin-label" htmlFor="oe-cad-modelo">
              Modelo
            </label>
            <input
              id="oe-cad-modelo"
              className="admin-input"
              type="text"
              value={modelo}
              onChange={(e) => setModelo(e.target.value)}
              placeholder="Ex: Gol 1.0"
            />
          </div>
          <div className="admin-input-group">
            <label className="admin-label" htmlFor="oe-cad-cor">
              Cor
            </label>
            <input
              id="oe-cad-cor"
              className="admin-input"
              type="text"
              value={cor}
              onChange={(e) => setCor(e.target.value)}
              placeholder="Ex: Prata"
            />
          </div>
          <div className="admin-input-group curto">
            <label className="admin-label" htmlFor="oe-cad-ano">
              Ano/modelo
            </label>
            <input
              id="oe-cad-ano"
              className="admin-input"
              type="text"
              value={anoModelo}
              maxLength={10}
              onChange={(e) => setAnoModelo(e.target.value)}
              placeholder="Ex: 2019/2020"
            />
          </div>
        </div>

        <div className="admin-form-grid" style={{ marginTop: 16 }}>
          <div className="admin-input-group curto">
            <label className="admin-label oe-campo-obrigatorio" htmlFor="oe-cad-entrada">
              Entrada na oficina
            </label>
            <input
              id="oe-cad-entrada"
              className="admin-input"
              type="date"
              max={hojeIso()}
              value={entradaEm}
              onChange={(e) => setEntradaEm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="oe-form-rodape">
        <button
          type="button"
          className="admin-btn admin-btn-secondary"
          disabled={salvando}
          onClick={onCancelar}
        >
          Cancelar
        </button>
        <button type="button" className="admin-btn admin-btn-primary" disabled={salvando} onClick={salvar}>
          {salvando ? 'Gravando...' : 'Salvar cadastro'}
        </button>
      </div>
    </div>
  );
};

export default CadastroForm;
